import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import {
  filterArrayEmpty,
  getAseResultKey,
  getChecklistOutcome,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  eventAseSelectFindingQuestion,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SET_INCOMING_VALUE,
} from 'store/app/actions';
import {
  selectAseTplSections,
  selectAseResultQuestions,
  selectAseTplAnswers,
  selectAseTplQuestions,
  selectAseTplSectionIds,
  selectAseResultChecklistItems,
  selectAseFieldInstanceAstAse,
  selectAseTplFieldGroup,
  selectAseTemplateFieldGroupConfig,
  selectAseTplChecklistItems,
  selectAseVisibleQuestionResults,
} from 'store/assessments/selectors';
import {
  AssessmentItemTypes,
  ChecklistItemResult,
} from 'store/assessments/types';
import { AssessmentQuestionType } from 'types/types';

export const BelongsToScreen: React.FC<
  ModalScreenProps<ModalScreens.ASEFINDINGBELONGSTO>
> = ({ route }) => {
  const dispatch = useDispatch();
  const {
    fieldInstanceId,
    findingType,
    incomingValueKey,
    sectionId,
    questionId,
    eventKey,
  } = route.params;

  const fieldInstance = useSelector((state) =>
    selectAseFieldInstanceAstAse(state, fieldInstanceId || '-1'),
  );
  const fieldGroup = useSelector((state) =>
    selectAseTplFieldGroup(state, fieldInstance?.groupId || '-1'),
  );
  const fieldGroupConfig = useSelector((state) =>
    selectAseTemplateFieldGroupConfig(state, fieldGroup?.id || '-1'),
  );

  const templateQuestions = useSelector(selectAseTplQuestions);
  const templateAnswers = useSelector(selectAseTplAnswers);
  const templateSectionIds = useSelector(selectAseTplSectionIds);
  const templateSections = useSelector(selectAseTplSections);
  const templateChecklistItems = useSelector(selectAseTplChecklistItems);
  const resultQuestions = useSelector(selectAseResultQuestions);
  const resultChecklistItems = useSelector(selectAseResultChecklistItems);
  const questionIsVisible = useSelector(selectAseVisibleQuestionResults);
  const handleGoBack = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const handlePress = React.useCallback(
    (moveId: string, moveToType: 'section' | 'question' | 'instance') => {
      if (moveToType === 'instance') {
        if (eventKey) {
          dispatch(
            eventAseSelectFindingQuestion({
              key: eventKey,
              data: {
                questionId: null,
                sectionId: moveId,
              },
            }),
          );
        }
        if (incomingValueKey) {
          dispatch(
            SET_INCOMING_VALUE({
              key: incomingValueKey,
              value: {
                questionId: null,
                sectionId: moveId,
              },
            }),
          );
        }
        handleGoBack();
        return;
      }
      if (moveToType === 'section') {
        if (eventKey) {
          dispatch(
            eventAseSelectFindingQuestion({
              key: eventKey,
              data: {
                questionId: null,
                sectionId: moveId,
              },
            }),
          );
        }
        if (incomingValueKey) {
          dispatch(
            SET_INCOMING_VALUE({
              key: incomingValueKey,
              value: {
                questionId: null,
                sectionId: moveId,
              },
            }),
          );
        }
        handleGoBack();
        return;
      }
      const question = templateQuestions[moveId]!;

      // Checklist
      if (question.type === AssessmentQuestionType.checklist) {
        if (
          !question.checklistItemIds?.find(
            (checklistItemId) =>
              !!resultChecklistItems[
                getAseResultKey(checklistItemId, fieldInstance?.instanceId)
              ],
          )
        ) {
          dispatch(
            NAVIGATE_MODAL({
              screen: ModalScreens.AseQuickAnswerChecklist,
              params: {
                instructions: 'Complete a checklist item to move here',
                questionId: moveId,
                fieldInstanceId,
                actionsAfter: [
                  eventKey
                    ? eventAseSelectFindingQuestion({
                        key: eventKey,
                        data: {
                          questionId: moveId,
                          sectionId: null,
                        },
                      })
                    : null,
                  incomingValueKey
                    ? SET_INCOMING_VALUE({
                        key: incomingValueKey,
                        value: {
                          questionId: moveId,
                          sectionId: null,
                        },
                      })
                    : null,
                  NAVIGATE_MODAL_BACK(),
                ].filter(filterArrayEmpty),
              },
            }),
          );
          return;
        }
      } else if (!resultQuestions[moveId]?.answerId) {
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.AseQuickAnswerQuestion,
            params: {
              instructions: 'Answer this question to move here',
              questionId: moveId,
              fieldInstanceId,
              actionsAfter: [
                eventKey
                  ? eventAseSelectFindingQuestion({
                      key: eventKey,
                      data: {
                        questionId: moveId,
                        sectionId: null,
                      },
                    })
                  : null,
                incomingValueKey
                  ? SET_INCOMING_VALUE({
                      key: incomingValueKey,
                      value: {
                        questionId: moveId,
                        sectionId: null,
                      },
                    })
                  : null,
                NAVIGATE_MODAL_BACK(),
              ].filter(filterArrayEmpty),
            },
          }),
        );
        return;
      }
      if (eventKey) {
        dispatch(
          eventAseSelectFindingQuestion({
            key: eventKey,
            data: {
              questionId: moveId,
              sectionId: null,
            },
          }),
        );
      }
      if (incomingValueKey) {
        dispatch(
          SET_INCOMING_VALUE({
            key: incomingValueKey,
            value: {
              questionId: moveId,
              sectionId: null,
            },
          }),
        );
      }
      handleGoBack();
    },
    [
      dispatch,
      eventKey,
      fieldInstance?.instanceId,
      fieldInstanceId,
      handleGoBack,
      incomingValueKey,
      resultChecklistItems,
      resultQuestions,
      templateQuestions,
    ],
  );

  useEsc(handleGoBack);

  const handleGetQuestionCardRow = React.useCallback(
    (qId: string) => {
      if (!questionIsVisible[getAseResultKey(qId, fieldInstance?.instanceId)]) {
        return null;
      }
      const question = templateQuestions[qId]!;
      const answer =
        question.type === AssessmentQuestionType.question
          ? templateAnswers[resultQuestions?.[question.id]?.answerId || '-1']
          : undefined;
      const checklistOutcome =
        question.type === AssessmentQuestionType.checklist
          ? getChecklistOutcome(
              false,
              question.checklistItemIds?.map(
                (checklistItemId) => templateChecklistItems[checklistItemId]!,
              ) || [],
              question.checklistItemIds?.reduce<
                Record<string, ChecklistItemResult>
              >((acc, checklistItemId) => {
                const checklistItemResult =
                  resultChecklistItems[
                    getAseResultKey(checklistItemId, fieldInstance?.instanceId)
                  ];
                if (checklistItemResult) {
                  acc[`${checklistItemId}`] = checklistItemResult;
                }
                return acc;
              }, {}) || {},
              true,
            )
          : undefined;

      return (
        <CardRow
          key={`q-${qId}`}
          disabled={qId === questionId}
          icon={
            question.type === AssessmentQuestionType.checklist
              ? ICONS.CircleCSolid
              : ICONS.CircleQSolid
          }
          iconColor={
            question.type === AssessmentQuestionType.checklist
              ? theme.colors.checklist
              : theme.colors.question
          }
          label={question.question}
          labelAlignTop
          labelNumberOfLines={0}
          value={
            question.type === AssessmentQuestionType.checklist
              ? `${checklistOutcome?.complete || 0} of ${pluralise(
                  checklistOutcome?.total || 0,
                  '{x} Checklist Item Answered',
                  '{x} Checklist Items Answered',
                )}`
              : answer
                ? answer.answer
                : 'No Answer'
          }
          valueColor={
            question.type === AssessmentQuestionType.checklist
              ? !checklistOutcome?.complete
                ? theme.colors.placeholder
                : undefined
              : !answer
                ? theme.colors.placeholder
                : undefined
          }
          onPress={() => handlePress(question.id, 'question')}
        />
      );
    },
    [
      fieldInstance?.instanceId,
      handlePress,
      questionId,
      questionIsVisible,
      resultChecklistItems,
      resultQuestions,
      templateAnswers,
      templateChecklistItems,
      templateQuestions,
    ],
  );

  return (
    <PageScrollView
      header={
        <NavigationHeader
          onPressSaveButton={handleGoBack}
          onPressCancelButton={handleGoBack}
          saveButtonText="Done"
          heading={`Move ${
            findingType === AssessmentItemTypes.ACTION ? 'Action' : 'Control'
          }`}
        />
      }
    >
      {fieldGroup && fieldGroupConfig && fieldInstance ? (
        <Card key="card">
          <CardRow
            key="instance"
            label={fieldGroup.nameSingular}
            labelNumberOfLines={0}
            labelWeight="bold"
            onPress={() => handlePress('1', 'instance')}
          />
          {fieldGroupConfig.questionIds.map(handleGetQuestionCardRow)}
        </Card>
      ) : (
        templateSectionIds.map((sId) => {
          const section = templateSections[sId]!;
          return (
            <Card key={section.id}>
              <CardRow
                key={`s-${section.id}`}
                disabled={sId === sectionId}
                label={section.name}
                labelNumberOfLines={0}
                labelWeight="bold"
                onPress={() => handlePress(section.id, 'section')}
              />
              {section.question_ids.map(handleGetQuestionCardRow)}
            </Card>
          );
        })
      )}
    </PageScrollView>
  );
};
